import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Helper per ottenere la data locale in formato YYYY-MM-DD senza conversione UTC
const getLocalDateString = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default function PlanningOrario({ user }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [sedi, setSedi] = useState([]);
  const [tipologie, setTipologie] = useState([]);
  const [turni, setTurni] = useState([]);
  const [operatori, setOperatori] = useState([]);
  const [richieste, setRichieste] = useState([]);
  const [richiesteViste, setRichiesteViste] = useState(new Set());
  
  // FILTRI NUOVI
  const [filtroOperatori, setFiltroOperatori] = useState([]);
  const [filtroSedi, setFiltroSedi] = useState([]);
  const [filtroTipologie, setFiltroTipologie] = useState([]);
  
  // REPORT
  const [activeView, setActiveView] = useState('calendar'); // 'calendar' | 'report'
  const [reportData, setReportData] = useState(null);
  const [reportPeriod, setReportPeriod] = useState({
    data_inizio: getLocalDateString(),
    data_fine: getLocalDateString()
  });
  const [reportFilters, setReportFilters] = useState({
    operatore_id: '',
    sede_id: '',
    tipologia_id: ''
  });
  
  // Dialog states
  const [showTurnoDialog, setShowTurnoDialog] = useState(false);
  const [showGestioneDialog, setShowGestioneDialog] = useState(false);
  const [showRichiestaDialog, setShowRichiestaDialog] = useState(false);
  const [showRichiesteListDialog, setShowRichiesteListDialog] = useState(false);
  const [showPubblicaDialog, setShowPubblicaDialog] = useState(false);
  const [showReplicaDialog, setShowReplicaDialog] = useState(false);
  
  //  states
  const [selectedTurno, setSelectedTurno] = useState(null);
  const [editingTipologia, setEditingTipologia] = useState(null);
  const [editingSede, setEditingSede] = useState(null);
  const [turniSelezionati, setTurniSelezionati] = useState([]); // Per pubblicazione multipla
  
  // Forms
  const [turnoForm, setTurnoForm] = useState({
    tipologia_turno_id: '',
    data: '',
    operatore_id: '',
    sede_id: '',
    emoji: '',
    note: ''
  });
  
  const [tipologiaForm, setTipologiaForm] = useState({
    nome: '',
    ora_inizio: '08:00',
    ora_fine: '14:00',
    colore: '#3b82f6',
    emoji: '',
    sede_id: '',
    durata_ore: 6,
    pausa_minuti: 30
  });
  
  const [sedeForm, setSedeForm] = useState({ nome: '', indirizzo: '' });
  const [richiestaForm, setRichiestaForm] = useState({
    operatore_id: '',
    planning_role: 'viewer',
    motivo: ''
  });
  
  // Helper per ottenere emoji tipologia
  const getTipologiaEmoji = (nomeTipologia) => {
    const nome = nomeTipologia?.toLowerCase() || '';
    if (nome.includes('mattina')) return '☀️';
    if (nome.includes('pomeriggio')) return '🌤️';
    if (nome.includes('notte')) return '🌙';
    if (nome.includes('reperibilità') || nome.includes('reperibilita')) return '📱';
    return '⏰';
  };
  
  // Helper per ottenere sigla tipologia
  const getTipologiaSigla = (nomeTipologia) => {
    const nome = nomeTipologia?.toLowerCase() || '';
    if (nome.includes('mattina')) return 'M';
    if (nome.includes('pomeriggio')) return 'P';
    if (nome.includes('notte')) return 'N';
    if (nome.includes('reperibilità') || nome.includes('reperibilita')) return 'REP';
    return 'T';
  };
  
  const isCoordOrAdmin = () => {
    return user.planning_role === 'coordinator' || user.role === 'super_amministratore';
  };
  
  const isSuperAdmin = () => user.role === 'super_amministratore';
  
  const fetchAll = useCallback(async () => {
    await Promise.all([
      fetchSedi(),
      fetchTipologie(),
      fetchOperatori(),
      fetchTurni(),
      isCoordOrAdmin() && fetchRichieste()
    ]);
  }, [currentDate]); // fetch functions deps are stable via useCallback
  
  useEffect(() => {
    fetchAll();
  }, [fetchAll]);
  
  const fetchSedi = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/planning/sedi`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSedi(res.data);
    } catch (err) {
      console.error('Errore caricamento sedi:', err);
    }
  };
  
  const fetchTipologie = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/planning/tipologie`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTipologie(res.data);
    } catch (err) {
      console.error('Errore caricamento tipologie:', err);
    }
  };
  
  const fetchOperatori = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/planning/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOperatori(res.data);
    } catch (err) {
      toast.error('Errore nel caricamento degli operatori');
    }
  };
  
  const fetchTurni = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error('Sessione scaduta. Effettua nuovamente il login.');
        return;
      }
      
      // Solo vista settimanale - dal lunedì alla domenica
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const day = currentDate.getDate();
      const dayOfWeek = currentDate.getDay();
      const diff = day - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Lunedì
      
      const monday = new Date(year, month, diff);
      const sunday = new Date(monday);
      sunday.setDate(sunday.getDate() + 6);
      
      const firstDay = getLocalDateString(monday);
      const lastDay = getLocalDateString(sunday);
      
      const params = { 
        data_inizio: firstDay, 
        data_fine: lastDay,
        _v: 'v2.1', // Version marker
        _t: Date.now() // Cache busting
      };
      
      // Se operatore (non coordinatore), filtra per i propri turni
      if (user.planning_role === 'viewer' && user.role !== 'super_amministratore') {
        params.operatore_id = user.id;
      }
      
      const res = await axios.get(`${API}/api/planning/turni/range-v2`, {
        params,
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      setTurni(res.data);
    } catch (err) {
      
      if (err.response?.status === 401) {
        toast.error('Sessione scaduta. Effettua nuovamente il login.');
        // Logout automatico
        localStorage.removeItem('token');
        window.location.href = '/';
      } else if (err.response?.status === 403) {
        toast.error('Accesso negato. Verifica di avere i permessi Planning Orario.');
      } else if (err.response?.status === 404) {
        toast.error('Endpoint turni non trovato (404). Possibile problema di autenticazione - prova a rifare il login.');
      } else {
        toast.error(`Errore nel caricamento dei turni (${err.response?.status || 'Network'}). ${err.response?.data?.detail || err.message}`);
      }
    }
  };
  
  const fetchRichieste = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/planning/richieste-accesso`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRichieste(res.data);
    } catch (err) {
      console.error('Errore caricamento richieste:', err);
    }
  };
  
  const goToPreviousWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentDate(newDate);
  };
  
  const goToNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentDate(newDate);
  };
  
  const goToToday = () => setCurrentDate(new Date());
  
  const handleSaveTurno = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Prepara i dati
      const payload = {
        tipologia_turno_id: turnoForm.tipologia_turno_id,
        data: turnoForm.data,
        operatore_id: turnoForm.operatore_id,
        note: turnoForm.note || ''
      };
      
      // Aggiungi sede_id solo se presente
      if (turnoForm.sede_id && turnoForm.sede_id !== '') {
        payload.sede_id = turnoForm.sede_id;
      }
      
      // Aggiungi emoji solo se presente
      if (turnoForm.emoji && turnoForm.emoji.trim() !== '') {
        payload.emoji = turnoForm.emoji.trim();
      }
      
      if (selectedTurno) {
        await axios.put(
          `${API}/api/planning/turni/${selectedTurno.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Turno modificato');
      } else {
        await axios.post(
          `${API}/api/planning/turni`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Turno creato');
      }
      setShowTurnoDialog(false);
      setTurnoForm({ tipologia_turno_id: '', data: '', operatore_id: '', sede_id: '', emoji: '', note: '' });
      setSelectedTurno(null);
      fetchTurni();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio del turno');
    }
  };
  
  const handleDeleteTurno = async (turnoId) => {
    if (!window.confirm('Annullare questo turno?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/planning/turni/${turnoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Turno annullato');
      fetchTurni();
    } catch (err) {
      toast.error('Errore nell\'annullamento');
    }
  };
  
  const openNewTurnoDialog = (date, operatoreId) => {
    setSelectedTurno(null);
    setTurnoForm({
      tipologia_turno_id: '',
      data: date,
      operatore_id: operatoreId,
      sede_id: '',
      note: ''
    });
    setShowTurnoDialog(true);
  };
  
  const openEditTurnoDialog = (turno) => {
    setSelectedTurno(turno);
    setTurnoForm({
      tipologia_turno_id: turno.tipologia_turno_id,
      data: turno.data,
      operatore_id: turno.operatore_id,
      sede_id: turno.sede_id || '',
      emoji: turno.emoji || '',
      note: turno.note || ''
    });
    setShowTurnoDialog(true);
  };
  
  const handleSaveTipologia = async () => {
    try {
      const token = localStorage.getItem('token');
      if (editingTipologia) {
        await axios.put(
          `${API}/api/planning/tipologie/${editingTipologia.id}`,
          tipologiaForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Tipologia modificata');
      } else {
        await axios.post(
          `${API}/api/planning/tipologie`,
          tipologiaForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Tipologia creata');
      }
      setTipologiaForm({ nome: '', ora_inizio: '08:00', ora_fine: '14:00', colore: '#3b82f6', durata_ore: 6, pausa_minuti: 30 });
      setEditingTipologia(null);
      fetchTipologie();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    }
  };
  
  const handleSaveSede = async () => {
    try {
      const token = localStorage.getItem('token');
      if (editingSede) {
        await axios.put(
          `${API}/api/planning/sedi/${editingSede.id}`,
          sedeForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Sede modificata');
      } else {
        await axios.post(
          `${API}/api/planning/sedi`,
          sedeForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Sede creata');
      }
      setSedeForm({ nome: '', indirizzo: '' });
      setEditingSede(null);
      fetchSedi();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    }
  };
  
  const handleCreateRichiesta = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/planning/richieste-accesso`,
        richiestaForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Richiesta inviata al Super Admin');
      setShowRichiestaDialog(false);
      setRichiestaForm({ operatore_id: '', planning_role: 'viewer', motivo: '' });
      if (isSuperAdmin()) fetchRichieste();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nell\'invio');
    }
  };
  
  const handleApprovaRichiesta = async (richiestaId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/planning/richieste-accesso/${richiestaId}/approva`,
        null,
        { params: { risposta: 'Approvato' }, headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Richiesta approvata');
      fetchRichieste();
      fetchOperatori();
      
      // Trigger evento per aggiornare lista utenti nel tab Utenti
      window.dispatchEvent(new CustomEvent('refreshUserList'));
    } catch (err) {
      toast.error('Errore nell\'approvazione');
    }
  };
  
  const handleRifiutaRichiesta = async (richiestaId) => {
    const motivo = window.prompt('Inserisci il motivo del rifiuto:');
    if (!motivo) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/planning/richieste-accesso/${richiestaId}/rifiuta`,
        null,
        { params: { risposta: motivo }, headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Richiesta rifiutata');
      fetchRichieste();
    } catch (err) {
      toast.error('Errore nel rifiuto');
    }
  };
  
  // Genera giorni della settimana corrente
  const getWeekDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const day = currentDate.getDate();
    const dayOfWeek = currentDate.getDay();
    const diff = day - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Lunedì
    
    const monday = new Date(year, month, diff);
    const days = [];
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(monday);
      date.setDate(date.getDate() + i);
      days.push(date);
    }
    
    return days;
  };
  
  // Filtra operatori basati sui filtri selezionati
  const getFilteredOperatori = () => {
    let filtered = operatori;
    
    if (filtroOperatori.length > 0) {
      filtered = filtered.filter(op => filtroOperatori.includes(op.id));
    }
    
    return filtered;
  };
  
  // Filtra turni basati sui filtri selezionati
  const getFilteredTurni = (operatoreId, dateStr) => {
    let filtered = turni.filter(t => 
      t.data === dateStr && t.operatore_id === operatoreId
    );
    
    if (filtroSedi.length > 0) {
      filtered = filtered.filter(t => t.sede_id && filtroSedi.includes(t.sede_id));
    }
    
    if (filtroTipologie.length > 0) {
      filtered = filtered.filter(t => t.tipologia_turno_id && filtroTipologie.includes(t.tipologia_turno_id));
    }
    
    return filtered;
  };
  
  // Pulisci tutti i filtri
  const clearFilters = () => {
    setFiltroOperatori([]);
    setFiltroSedi([]);
    setFiltroTipologie([]);
  };
  
  // Carica report
  const fetchReport = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Usa il filtro mese (currentDate) per calcolare data_inizio e data_fine
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      
      const params = {
        data_inizio: getLocalDateString(firstDay),
        data_fine: getLocalDateString(lastDay),
        operatore_id: filtroOperatori[0] || '',
        sede_id: filtroSedi[0] || '',
        tipologia_id: filtroTipologie[0] || ''
      };
      
      const res = await axios.get(`${API}/api/planning/report`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setReportData(res.data);
      toast.success('Report generato');
    } catch (err) {
      toast.error('Errore nel caricamento del report');
    }
  };
  
  // Esporta Excel
  const exportExcel = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Usa il filtro mese (currentDate)
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      
      const params = new URLSearchParams({
        data_inizio: getLocalDateString(firstDay),
        data_fine: getLocalDateString(lastDay),
        operatore_id: filtroOperatori[0] || '',
        sede_id: filtroSedi[0] || '',
        tipologia_id: filtroTipologie[0] || ''
      });
      
      window.open(`${API}/api/planning/report/excel?${params}&token=${token}`, '_blank');
      toast.success('Download Excel avviato');
    } catch (err) {
      toast.error('Errore nell\'export Excel');
    }
  };
  
  // Esporta PDF
  const exportPDF = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Usa il filtro mese (currentDate)
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      
      const params = new URLSearchParams({
        data_inizio: getLocalDateString(firstDay),
        data_fine: getLocalDateString(lastDay),
        operatore_id: filtroOperatori[0] || '',
        sede_id: filtroSedi[0] || '',
        tipologia_id: filtroTipologie[0] || ''
      });
      
      window.open(`${API}/api/planning/report/pdf?${params}&token=${token}`, '_blank');
      toast.success('Download PDF avviato');
    } catch (err) {
      toast.error('Errore nell\'export PDF');
    }
  };
  
  // Pubblica turni selezionati
  const pubblicaTurniMultipli = async () => {
    if (turniSelezionati.length === 0) {
      toast.error('Seleziona almeno un turno da pubblicare');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/planning/turni/pubblica-multipli`, 
        turniSelezionati,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${turniSelezionati.length} turni pubblicati con successo`);
      setTurniSelezionati([]);
      setShowPubblicaDialog(false);
      await fetchTurni();
    } catch (err) {
      toast.error('Errore nella pubblicazione dei turni');
    }
  };
  
  // Replica turni settimana precedente
  const replicaTurniSettimana = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Calcola lunedì della settimana corrente
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const day = currentDate.getDate();
      const dayOfWeek = currentDate.getDay();
      const diff = day - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
      const monday = new Date(year, month, diff);
      const mondayStr = getLocalDateString(monday);
      
      const res = await axios.post(`${API}/api/planning/turni/replica-settimana`, 
        { data_inizio_settimana: mondayStr },
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { data_inizio_settimana: mondayStr }
        }
      );
      
      toast.success(res.data.message || 'Turni replicati con successo');
      setShowReplicaDialog(false);
      await fetchTurni();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella replica dei turni');
    }
  };
  
  // Toggle selezione turno per pubblicazione
  const toggleSelezioneTurno = (turnoId) => {
    setTurniSelezionati(prev => {
      if (prev.includes(turnoId)) {
        return prev.filter(id => id !== turnoId);
      } else {
        return [...prev, turnoId];
      }
    });
  };
  
  // Rendering CARD per operatore con GIORNI IN COLONNA (dentro card)
  const renderOperatoreCard = (operatore) => {
    const weekDays = getWeekDays();
    const today = getLocalDateString();
    
    return (
      <Card key={operatore.id} className="mb-3 shadow-sm hover:shadow-md transition-shadow">
        <CardHeader className="pb-2 pt-3 px-4 bg-gradient-to-r from-cyan-50 to-blue-50">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-bold text-gray-800">
                {operatore.first_name} {operatore.last_name}
              </div>
              <div className="text-xs text-gray-600">
                {operatore.figura_professionale || 'Operatore'}
              </div>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-3">
          {/* Tabella giorni in colonna */}
          <div className="space-y-1">
            {weekDays.map((date, dayIdx) => {
              const dateStr = getLocalDateString(date);
              const isToday = dateStr === today;
              const isSunday = dayIdx === 6;
              const dayName = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'][dayIdx];
              
              const turniGiorno = getFilteredTurni(operatore.id, dateStr);
              
              return (
                <div
                  key={dateStr}
                  className={`flex gap-2 p-2 rounded-lg border ${
                    isToday ? 'border-cyan-400 bg-cyan-50' : 
                    isSunday ? 'border-red-300 bg-red-50' : 
                    'border-gray-200 bg-white'
                  }`}
                >
                  {/* Colonna Giorno */}
                  <div className={`w-16 flex-shrink-0 text-center py-1 rounded font-bold text-xs ${
                    isToday ? 'bg-cyan-500 text-white' : 
                    isSunday ? 'bg-red-500 text-white' : 
                    'bg-gray-100 text-gray-700'
                  }`}>
                    <div className="text-[10px] font-normal">{dayName}</div>
                    <div>{date.getDate()}/{date.getMonth() + 1}</div>
                  </div>
                  
                  {/* Colonna Turni */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap gap-1">
                      {turniGiorno.map(turno => (
                        <div 
                          key={turno.id}
                          className="group relative flex flex-col gap-0.5 p-1.5 rounded-md border-l-4 hover:shadow-md transition-all cursor-pointer"
                          style={{
                            backgroundColor: turno.tipologia?.colore + '20' || '#3b82f620',
                            borderLeftColor: turno.tipologia?.colore || '#3b82f6',
                            opacity: turno.pubblicato ? 1 : 0.6
                          }}
                          onClick={() => {
                            if (isCoordOrAdmin()) {
                              openEditTurnoDialog(turno);
                            }
                          }}
                        >
                          {/* Badge BOZZA per coordinatori - ridotto */}
                          {!turno.pubblicato && isCoordOrAdmin() && (
                            <span className="absolute -top-0.5 -right-0.5 bg-amber-500 text-white text-[7px] font-bold px-0.5 py-0 rounded leading-tight">
                              BOZZA
                            </span>
                          )}
                          
                          {/* Riga 1: Icona */}
                          <div className="flex items-center justify-between">
                            <span className="text-base">
                              {turno.emoji || getTipologiaEmoji(turno.tipologia?.nome)}
                            </span>
                            {isCoordOrAdmin() && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteTurno(turno.id);
                                }}
                                className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                          
                          {/* Riga 2-3: Orari su due righe */}
                          <div className="flex flex-col text-[9px] text-gray-700 font-bold leading-tight">
                            <span>{turno.tipologia?.ora_inizio?.substring(0, 5)}</span>
                            <span>{turno.tipologia?.ora_fine?.substring(0, 5)}</span>
                          </div>
                          
                          {/* Riga 4-5: Descrizione e Sede su due righe */}
                          <div className="flex flex-col text-[9px] leading-tight">
                            <span className="font-bold truncate text-gray-800">
                              {turno.tipologia?.nome || 'Turno'}
                            </span>
                            {turno.sede && (
                              <span className="text-gray-500 truncate">
                                {turno.sede.nome}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                      
                      {/* Pulsante aggiungi compatto */}
                      {isCoordOrAdmin() && (
                        <button
                          onClick={() => {
                            setTurnoForm({
                              ...turnoForm,
                              data: dateStr,
                              operatore_id: operatore.id,
                              tipologia_turno_id: '',
                              sede_id: '',
                              emoji: ''
                            });
                            setShowTurnoDialog(true);
                          }}
                          className="inline-flex items-center px-2 py-1 border border-dashed border-gray-300 rounded text-gray-400 hover:border-cyan-400 hover:text-cyan-600 hover:bg-cyan-50 transition-all text-xs"
                        >
                          + Aggiungi
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  };
  
  const monthNames = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 
                      'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
  const richiesteCount = richieste.filter(r => r.stato === 'pending' && !richiesteViste.has(r.id)).length;
  
  // Lista operatori SENZA accesso planning per la richiesta
  const [allUsers, setAllUsers] = useState([]);
  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/api/users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAllUsers(res.data);
      } catch (error) {
        console.error('Errore caricamento utenti:', error);
      }
    };
    if (isCoordOrAdmin()) fetchAllUsers();
  }, []);
  
  const usersWithoutPlanning = allUsers.filter(u => 
    !u.has_planning_access && u.role === 'operatore'
  );
  
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4">
            {/* Titolo */}
            <div className="flex items-center gap-3">
              <div>
                <CardTitle className="text-xl sm:text-2xl">Planning Orario Settimanale</CardTitle>
                <p className="text-sm text-gray-600">Gestione turni con orari specifici</p>
              </div>
            </div>
            
            {/* Switch Vista Calendario/Report - Stile GestioneTurni */}
            <div className="flex gap-1 border-2 border-gray-200 rounded-lg bg-white h-9 items-center px-1 w-fit">
              <Button 
                variant={activeView === 'calendar' ? 'default' : 'ghost'}
                size="sm" 
                onClick={() => setActiveView('calendar')}
                className={`${activeView === 'calendar' ? 'bg-cyan-600 hover:bg-cyan-700' : 'hover:bg-cyan-50'} text-xs px-2 h-7`}>
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                <span className="hidden xs:inline">Calendario</span>
              </Button>
              <Button 
                variant={activeView === 'report' ? 'default' : 'ghost'}
                size="sm" 
                onClick={() => setActiveView('report')}
                className={`${activeView === 'report' ? 'bg-cyan-600 hover:bg-cyan-700' : 'hover:bg-cyan-50'} text-xs px-2 h-7`}>
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <span className="hidden xs:inline">Report</span>
              </Button>
            </div>
            
            {/* FILTRI SEMPRE VISIBILI */}
            <div className="space-y-2">
              <h3 className="font-bold text-sm text-gray-700">Filtri</h3>
              <div className="flex flex-wrap gap-2">
                {/* Filtro Operatori - Nascosto per operatori, visibile per coordinatori e super admin */}
                {(user.role === 'super_amministratore' || user.planning_role === 'coordinator') && (
                  <div className="relative flex-1 min-w-[200px]">
                    <select
                      className="w-full border-2 border-green-300 bg-white rounded-lg px-4 py-2 text-sm font-medium text-gray-700 appearance-none cursor-pointer hover:border-green-400 transition-colors pr-8"
                      value={filtroOperatori[0] || ''}
                      onChange={(e) => {
                        if (e.target.value === '') {
                          setFiltroOperatori([]);
                        } else {
                          setFiltroOperatori([e.target.value]);
                        }
                      }}
                    >
                      <option value="">Tutti gli operatori</option>
                      {operatori.map(op => (
                        <option key={op.id} value={op.id}>
                          {op.first_name} {op.last_name}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                      <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                )}
                
                {/* Filtro Sedi - Nascosto per operatori, visibile per coordinatori e super admin */}
                {(user.role === 'super_amministratore' || user.planning_role === 'coordinator') && (
                  <div className="relative flex-1 min-w-[200px]">
                    <select
                      className="w-full border-2 border-blue-300 bg-white rounded-lg px-4 py-2 text-sm font-medium text-gray-700 appearance-none cursor-pointer hover:border-blue-400 transition-colors pr-8"
                      value={filtroSedi[0] || ''}
                      onChange={(e) => {
                        if (e.target.value === '') {
                          setFiltroSedi([]);
                        } else {
                          setFiltroSedi([e.target.value]);
                        }
                      }}
                    >
                      <option value="">Tutte le sedi</option>
                      {sedi.map(sede => (
                        <option key={sede.id} value={sede.id}>
                          {sede.nome}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                      <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                )}
                
                {/* Filtro Tipologie - Bianco */}
                <div className="relative flex-1 min-w-[200px]">
                  <select
                    className="w-full border-2 border-yellow-300 bg-white rounded-lg px-4 py-2 text-sm font-medium text-gray-700 appearance-none cursor-pointer hover:border-yellow-400 transition-colors pr-8"
                    value={filtroTipologie[0] || ''}
                    onChange={(e) => {
                      if (e.target.value === '') {
                        setFiltroTipologie([]);
                      } else {
                        setFiltroTipologie([e.target.value]);
                      }
                    }}
                  >
                    <option value="">Tutte le tipologie</option>
                    {tipologie.map(tip => (
                      <option key={tip.id} value={tip.id}>
                        {tip.nome}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
                
                {/* Filtro Mese - Bianco */}
                <div className="relative flex-1 min-w-[200px]">
                  <select
                    className="w-full border-2 border-purple-300 bg-white rounded-lg px-4 py-2 text-sm font-medium text-gray-700 appearance-none cursor-pointer hover:border-purple-400 transition-colors pr-8"
                    value={`${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`}
                    onChange={(e) => {
                      const [year, month] = e.target.value.split('-');
                      const newDate = new Date(parseInt(year), parseInt(month) - 1, 1);
                      setCurrentDate(newDate);
                    }}
                  >
                    {Array.from({length: 12}, (_, i) => {
                      const date = new Date();
                      date.setMonth(date.getMonth() - 6 + i);
                      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                      const label = date.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
                      return (
                        <option key={value} value={value}>
                          {label.charAt(0).toUpperCase() + label.slice(1)}
                        </option>
                      );
                    })}
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
                
                {/* Pulsante Refresh - rounded-lg come gli altri */}
                <button
                  onClick={clearFilters}
                  className="w-10 h-10 flex-shrink-0 rounded-lg border-2 border-gray-300 bg-white hover:bg-gray-50 flex items-center justify-center transition-colors"
                  title="Pulisci filtri"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Filtri Report - Solo Periodo (appare solo in report) */}
            
            {/* Bottoni azioni - Solo in vista Calendario */}
            {activeView === 'calendar' && (
              <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-2">
                {/* Richieste */}
                {isCoordOrAdmin() && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowRichiestaDialog(true)}
                      className="border-2 border-blue-300 text-blue-700 hover:bg-blue-50 font-medium h-10 w-full justify-center">
                      <span className="text-base mr-1">👤</span>
                      <span className="text-xs sm:text-sm">Richiedi Accesso</span>
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowReplicaDialog(true)}
                      className="border-2 border-orange-300 text-orange-700 hover:bg-orange-50 font-medium h-10 w-full justify-center">
                      <span className="text-base mr-1">📋</span>
                      <span className="text-xs sm:text-sm">Replica Settimana</span>
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        // Seleziona tutti i turni in bozza visibili
                        const turniBozza = turni.filter(t => !t.pubblicato).map(t => t.id);
                        setTurniSelezionati(turniBozza);
                        setShowPubblicaDialog(true);
                      }}
                      className="border-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-medium h-10 w-full justify-center">
                      <span className="text-base mr-1">✓</span>
                      <span className="text-xs sm:text-sm">Pubblica Turni</span>
                    </Button>
                  </>
                )}
                
                {isSuperAdmin() && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowRichiesteListDialog(true)}
                      className="border-2 border-green-300 text-green-700 hover:bg-green-50 font-medium h-10 w-full justify-center relative">
                      <span className="text-base mr-1">✅</span>
                      <span className="text-xs sm:text-sm">Gestisci Richieste</span>
                      {richiesteCount > 0 && (
                        <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                          {richiesteCount}
                        </span>
                      )}
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowGestioneDialog(true)}
                      className="border-2 border-purple-300 text-purple-700 hover:bg-purple-50 font-medium h-10 w-full justify-center">
                      <span className="text-base mr-1">⚙️</span>
                      <span className="text-xs sm:text-sm">Gestione</span>
                    </Button>
                  </>
                )}
              </div>
            )}
            
            {/* Navigazione Settimana - Solo in vista Calendario */}
            {activeView === 'calendar' && (
              <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-3 w-full border-t-2 border-gray-200 pt-4">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={goToPreviousWeek} className="border-2 border-gray-300 hover:bg-gray-100 px-2 h-8">
                  <ChevronLeft className="w-4 h-4" />
                  <span className="hidden sm:inline ml-1 text-xs">Prec.</span>
                </Button>
                
                <div className="bg-white border-2 border-cyan-600 text-cyan-600 px-3 py-1.5 rounded-lg font-bold text-xs h-8 flex items-center">
                  {(() => {
                    const weekDays = getWeekDays();
                    const monday = weekDays[0];
                    const sunday = weekDays[6];
                    return `${monday.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })} - ${sunday.toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' })}`;
                  })()}
                </div>
                
                <Button variant="outline" size="sm" onClick={goToNextWeek} className="border-2 border-gray-300 hover:bg-gray-100 px-2 h-8">
                  <span className="hidden sm:inline mr-1 text-xs">Succ.</span>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={fetchAll} className="border-2 border-green-300 hover:bg-green-50 text-green-700 w-8 h-8 p-0 flex items-center justify-center font-medium" title="Ricarica dati">
                  🔄
                </Button>
              </div>
            </div>
            )}
          </div>
        </CardHeader>
        
        <CardContent className="space-y-3 pt-4">
          {activeView === 'calendar' ? (
            <>
              {/* RENDER CARD PER OPERATORI */}
              {getFilteredOperatori().length === 0 ? (
                <div className="text-center py-8 text-gray-500 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg">
                  <p className="text-sm font-semibold">Nessun operatore trovato</p>
                  <p className="text-xs mt-1">Verifica i filtri</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {getFilteredOperatori().map(operatore => renderOperatoreCard(operatore))}
                </div>
              )}
              
              {/* Legenda sotto il calendario */}
              <div className="border-t-2 border-gray-200 pt-4 mt-6">
                <h3 className="font-bold text-sm mb-3 flex items-center gap-2">
                  <span>🎨</span>
                  <span>Legenda Tipologie Turno</span>
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {(() => {
                    // Filtra solo le tipologie presenti nei turni della settimana corrente
                    const tipologieIds = new Set(turni.map(t => t.tipologia_turno_id));
                    const tipologieFiltrate = tipologie.filter(tip => tipologieIds.has(tip.id));
                    
                    return tipologieFiltrate.length > 0 ? (
                      tipologieFiltrate.map(tip => (
                        <div key={tip.id} className="flex items-center gap-2 p-2 rounded-lg border-2 border-gray-200 hover:shadow-md transition-shadow">
                          <div className="flex items-center gap-1">
                            <span className="text-lg">{tip.emoji || getTipologiaEmoji(tip.nome)}</span>
                            <div
                              className="w-6 h-6 rounded flex-shrink-0 font-bold text-white text-xs flex items-center justify-center"
                              style={{backgroundColor: tip.colore}}
                            >
                              {getTipologiaSigla(tip.nome)}
                            </div>
                          </div>
                          <div className="text-xs">
                            <div className="font-semibold">{tip.nome}</div>
                            <div className="text-[10px] text-gray-600">
                              {tip.ora_inizio} - {tip.ora_fine}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="col-span-full text-center py-4 text-gray-500 text-sm">
                        Nessun turno programmato in questa settimana
                      </div>
                    );
                  })()}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* VISTA REPORT */}
              <div className="space-y-4">
                {/* Pulsanti Azione Report */}
                <div className="flex flex-wrap gap-2 items-center justify-between border-b-2 border-gray-200 pb-3">
                  <h3 className="font-bold text-gray-700">Report Mensile - {currentDate.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}</h3>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={fetchReport} className="bg-purple-600 hover:bg-purple-700">
                      📊 Genera Report
                    </Button>
                    <Button size="sm" variant="outline" onClick={exportExcel} className="border-green-500 text-green-700 hover:bg-green-50">
                      📗 Excel
                    </Button>
                    <Button size="sm" variant="outline" onClick={exportPDF} className="border-red-500 text-red-700 hover:bg-red-50">
                      📕 PDF
                    </Button>
                  </div>
                </div>
                
                {/* Risultati Report */}
                {reportData ? (
                  <div className="space-y-4">
                    {/* Totali */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-cyan-100 p-4 rounded-lg border-2 border-cyan-300">
                        <div className="text-2xl font-bold text-cyan-700">{reportData.totale_ore.toFixed(1)}</div>
                        <div className="text-sm text-gray-600">Ore Totali</div>
                      </div>
                      <div className="bg-blue-100 p-4 rounded-lg border-2 border-blue-300">
                        <div className="text-2xl font-bold text-blue-700">{reportData.totale_turni}</div>
                        <div className="text-sm text-gray-600">Turni Totali</div>
                      </div>
                    </div>
                    
                    {/* Tabella Ore per Operatore CON DETTAGLIO TIPOLOGIE */}
                    <div>
                      <h3 className="font-bold mb-2">Ore per Operatore (con dettaglio tipologie)</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse border text-sm">
                          <thead>
                            <tr className="bg-cyan-600 text-white">
                              <th className="border p-2 text-left">Operatore</th>
                              <th className="border p-2 text-center">Ore Totali</th>
                              <th className="border p-2 text-center">N° Turni</th>
                              <th className="border p-2 text-left">Dettaglio per Tipologia</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportData.ore_per_operatore.map((op, idx) => (
                              <tr key={op.operatore || idx} className="hover:bg-gray-50">
                                <td className="border p-2">{op.operatore}</td>
                                <td className="border p-2 text-center font-bold">{op.ore_totali.toFixed(1)}</td>
                                <td className="border p-2 text-center">{op.turni_count}</td>
                                <td className="border p-2">
                                  <div className="flex flex-wrap gap-2">
                                    {Object.entries(op.per_tipologia).map(([tipNome, tipData]) => (
                                      <span key={tipNome} className="text-xs bg-gray-100 px-2 py-1 rounded">
                                        <span className="font-semibold">{tipNome}:</span> {tipData.ore}h ({tipData.turni} turni)
                                      </span>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    
                    {/* Tabella Ore per Tipologia */}
                    <div>
                      <h3 className="font-bold mb-2">Riepilogo per Tipologia Turno</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse border text-sm">
                          <thead>
                            <tr className="bg-purple-600 text-white">
                              <th className="border p-2 text-left">Tipologia</th>
                              <th className="border p-2 text-center">Ore Totali</th>
                              <th className="border p-2 text-center">N° Turni</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportData.ore_per_tipologia.map((tip, idx) => (
                              <tr key={tip.tipologia || idx} className="hover:bg-gray-50">
                                <td className="border p-2">
                                  <span className="inline-block w-4 h-4 rounded mr-2" style={{backgroundColor: tip.colore}}></span>
                                  {tip.tipologia}
                                </td>
                                <td className="border p-2 text-center font-bold">{tip.ore_totali.toFixed(1)}</td>
                                <td className="border p-2 text-center">{tip.turni_count}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <p className="text-lg font-semibold">Imposta periodo e filtri sopra, poi clicca "📊 Genera Report"</p>
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
      
      {/* Dialog Turno */}
      <Dialog open={showTurnoDialog} onOpenChange={setShowTurnoDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedTurno ? 'Modifica Turno' : 'Nuovo Turno'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Tipologia Turno *</Label>
              <select
                className="w-full border rounded px-3 py-2"
                value={turnoForm.tipologia_turno_id}
                onChange={(e) => {
                  const selectedTipologia = tipologie.find(t => t.id === e.target.value);
                  setTurnoForm({
                    ...turnoForm, 
                    tipologia_turno_id: e.target.value,
                    // Pre-carica emoji e sede dalla tipologia se non sono già impostate
                    emoji: turnoForm.emoji || selectedTipologia?.emoji || '',
                    sede_id: turnoForm.sede_id || selectedTipologia?.sede_id || ''
                  });
                }}>
                <option value="">Seleziona...</option>
                {tipologie.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.emoji || getTipologiaEmoji(t.nome)} {t.nome} ({t.ora_inizio} - {t.ora_fine})
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <Label>Emoji Personalizzata</Label>
              <Input
                type="text"
                maxLength={2}
                value={turnoForm.emoji}
                onChange={(e) => setTurnoForm({...turnoForm, emoji: e.target.value})}
                placeholder="Es: ☀️ 🌙 📱 (opzionale)"
                className="text-lg"
              />
              <p className="text-xs text-gray-500 mt-1">Lascia vuoto per usare l'emoji della tipologia</p>
            </div>
            
            <div>
              <Label>Data *</Label>
              <Input
                type="date"
                value={turnoForm.data}
                onChange={(e) => setTurnoForm({...turnoForm, data: e.target.value})}
              />
            </div>
            <div>
              <Label>Operatore *</Label>
              <select
                className="w-full border rounded px-3 py-2"
                value={turnoForm.operatore_id}
                onChange={(e) => setTurnoForm({...turnoForm, operatore_id: e.target.value})}>
                <option value="">Seleziona...</option>
                {operatori.map(op => (
                  <option key={op.id} value={op.id}>
                    {op.first_name} {op.last_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>Sede</Label>
              <select
                className="w-full border rounded px-3 py-2"
                value={turnoForm.sede_id}
                onChange={(e) => setTurnoForm({...turnoForm, sede_id: e.target.value})}>
                <option value="">Nessuna sede</option>
                {sedi.map(s => (
                  <option key={s.id} value={s.id}>{s.nome}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Note</Label>
              <textarea
                className="w-full border rounded px-3 py-2"
                rows={3}
                value={turnoForm.note}
                onChange={(e) => setTurnoForm({...turnoForm, note: e.target.value})}
                placeholder="Note opzionali..."
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowTurnoDialog(false)}>Annulla</Button>
            <Button
              onClick={handleSaveTurno}
              disabled={!turnoForm.tipologia_turno_id || !turnoForm.data || !turnoForm.operatore_id}>
              {selectedTurno ? 'Salva' : 'Crea'}
            </Button>
            {selectedTurno && (
              <Button variant="destructive" onClick={() => {
                handleDeleteTurno(selectedTurno.id);
                setShowTurnoDialog(false);
              }}>
                Annulla Turno
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog Gestione (Super Admin) */}
      {isSuperAdmin() && (
        <Dialog open={showGestioneDialog} onOpenChange={setShowGestioneDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Gestione Tipologie e Sedi</DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              {/* Tipologie */}
              <div>
                <h3 className="font-bold mb-3">📋 Tipologie Turno</h3>
                <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
                  {tipologie.map(tip => (
                    <div key={tip.id} className="flex items-center justify-between p-2 border rounded">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded" style={{backgroundColor: tip.colore}}></div>
                        <div className="text-sm">
                          <div className="font-semibold">{tip.nome}</div>
                          <div className="text-xs text-gray-600">
                            {tip.ora_inizio} - {tip.ora_fine} ({tip.durata_ore}h)
                          </div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingTipologia(tip);
                          setTipologiaForm({
                            nome: tip.nome,
                            ora_inizio: tip.ora_inizio,
                            ora_fine: tip.ora_fine,
                            colore: tip.colore,
                            emoji: tip.emoji || '',
                            sede_id: tip.sede_id || '',
                            durata_ore: tip.durata_ore,
                            pausa_minuti: tip.pausa_minuti
                          });
                        }}>
                        Modifica
                      </Button>
                    </div>
                  ))}
                </div>
                
                {/* Form tipologia */}
                <div className="border rounded p-3 bg-gray-50">
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div>
                      <Label className="text-xs">Nome</Label>
                      <Input
                        value={tipologiaForm.nome}
                        onChange={(e) => setTipologiaForm({...tipologiaForm, nome: e.target.value})}
                        placeholder="es. Mattina"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Colore</Label>
                      <Input
                        type="color"
                        value={tipologiaForm.colore}
                        onChange={(e) => setTipologiaForm({...tipologiaForm, colore: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Ora Inizio</Label>
                      <Input
                        type="time"
                        value={tipologiaForm.ora_inizio}
                        onChange={(e) => setTipologiaForm({...tipologiaForm, ora_inizio: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Ora Fine</Label>
                      <Input
                        type="time"
                        value={tipologiaForm.ora_fine}
                        onChange={(e) => setTipologiaForm({...tipologiaForm, ora_fine: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Emoji Predefinita</Label>
                      <Input
                        type="text"
                        maxLength={2}
                        value={tipologiaForm.emoji}
                        onChange={(e) => setTipologiaForm({...tipologiaForm, emoji: e.target.value})}
                        placeholder="Es: ☀️ 🌙 📱"
                        className="text-lg"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Sede Predefinita</Label>
                      <select
                        className="w-full border rounded px-3 py-2 text-sm"
                        value={tipologiaForm.sede_id}
                        onChange={(e) => setTipologiaForm({...tipologiaForm, sede_id: e.target.value})}>
                        <option value="">Nessuna (opzionale)</option>
                        {sedi.map(s => (
                          <option key={s.id} value={s.id}>{s.nome}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">Durata (ore)</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={tipologiaForm.durata_ore}
                        onChange={(e) => setTipologiaForm({...tipologiaForm, durata_ore: parseFloat(e.target.value)})}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Pausa (min)</Label>
                      <Input
                        type="number"
                        value={tipologiaForm.pausa_minuti}
                        onChange={(e) => setTipologiaForm({...tipologiaForm, pausa_minuti: parseInt(e.target.value)})}
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleSaveTipologia}>
                      {editingTipologia ? 'Salva Modifiche' : 'Crea Tipologia'}
                    </Button>
                    {editingTipologia && (
                      <Button size="sm" variant="outline" onClick={() => {
                        setEditingTipologia(null);
                        setTipologiaForm({ nome: '', ora_inizio: '08:00', ora_fine: '14:00', colore: '#3b82f6', emoji: '', sede_id: '', durata_ore: 6, pausa_minuti: 30 });
                      }}>
                        Annulla
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Sedi */}
              <div>
                <h3 className="font-bold mb-3">📍 Sedi</h3>
                <div className="space-y-2 mb-4 max-h-40 overflow-y-auto">
                  {sedi.map(sede => (
                    <div key={sede.id} className="flex items-center justify-between p-2 border rounded">
                      <div className="text-sm">
                        <div className="font-semibold">{sede.nome}</div>
                        <div className="text-xs text-gray-600">{sede.indirizzo}</div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingSede(sede);
                          setSedeForm({ nome: sede.nome, indirizzo: sede.indirizzo || '' });
                        }}>
                        Modifica
                      </Button>
                    </div>
                  ))}
                </div>
                
                {/* Form sede */}
                <div className="border rounded p-3 bg-gray-50">
                  <div className="space-y-2 mb-2">
                    <div>
                      <Label className="text-xs">Nome Sede</Label>
                      <Input
                        value={sedeForm.nome}
                        onChange={(e) => setSedeForm({...sedeForm, nome: e.target.value})}
                        placeholder="es. Pescara Centro"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Indirizzo</Label>
                      <Input
                        value={sedeForm.indirizzo}
                        onChange={(e) => setSedeForm({...sedeForm, indirizzo: e.target.value})}
                        placeholder="Via..."
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleSaveSede}>
                      {editingSede ? 'Salva' : 'Crea Sede'}
                    </Button>
                    {editingSede && (
                      <Button size="sm" variant="outline" onClick={() => {
                        setEditingSede(null);
                        setSedeForm({ nome: '', indirizzo: '' });
                      }}>
                        Annulla
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Dialog Richiesta Accesso */}
      <Dialog open={showRichiestaDialog} onOpenChange={setShowRichiestaDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Richiedi Accesso Planning</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Operatore *</Label>
              <select
                className="w-full border rounded px-3 py-2"
                value={richiestaForm.operatore_id}
                onChange={(e) => setRichiestaForm({...richiestaForm, operatore_id: e.target.value})}>
                <option value="">Seleziona...</option>
                {usersWithoutPlanning.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.first_name} {u.last_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>Ruolo *</Label>
              <select
                className="w-full border rounded px-3 py-2"
                value={richiestaForm.planning_role}
                onChange={(e) => setRichiestaForm({...richiestaForm, planning_role: e.target.value})}>
                <option value="viewer">Viewer (solo visualizzazione)</option>
                <option value="coordinator">Coordinator (gestione completa)</option>
              </select>
            </div>
            <div>
              <Label>Motivo</Label>
              <textarea
                className="w-full border rounded px-3 py-2"
                rows={3}
                value={richiestaForm.motivo}
                onChange={(e) => setRichiestaForm({...richiestaForm, motivo: e.target.value})}
                placeholder="Motivazione..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRichiestaDialog(false)}>Annulla</Button>
            <Button onClick={handleCreateRichiesta} disabled={!richiestaForm.operatore_id}>
              Invia Richiesta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog Richieste (Super Admin) */}
      {isSuperAdmin() && (
        <Dialog open={showRichiesteListDialog} onOpenChange={(open) => {
          setShowRichiesteListDialog(open);
          if (!open) {
            // Marca tutte come viste
            const newViste = new Set(richiesteViste);
            richieste.forEach(r => newViste.add(r.id));
            setRichiesteViste(newViste);
          }
        }}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Richieste Accesso Planning</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              {richieste.filter(r => r.stato === 'pending').length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Nessuna richiesta in attesa
                </div>
              ) : (
                richieste.filter(r => r.stato === 'pending').map(richiesta => {
                  const operatore = allUsers.find(u => u.id === richiesta.operatore_id);
                  return (
                    <div key={richiesta.id} className="border rounded p-3 space-y-2">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-semibold">
                            {operatore?.first_name} {operatore?.last_name}
                          </div>
                          <div className="text-sm text-gray-600">
                            Ruolo richiesto: {richiesta.planning_role}
                          </div>
                          {richiesta.motivo && (
                            <div className="text-sm mt-1">
                              <strong>Motivo:</strong> {richiesta.motivo}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleApprovaRichiesta(richiesta.id)}
                            className="bg-green-600 hover:bg-green-700">
                            ✓
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleRifiutaRichiesta(richiesta.id)}>
                            ✗
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Dialog Pubblica Turni */}
      {isCoordOrAdmin() && (
        <Dialog open={showPubblicaDialog} onOpenChange={setShowPubblicaDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Pubblica Turni</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Stai per pubblicare <strong>{turniSelezionati.length}</strong> turni in bozza.
                I turni pubblicati diventeranno visibili agli operatori.
              </p>
              {turniSelezionati.length === 0 && (
                <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                  ⚠️ Nessun turno in bozza trovato. Tutti i turni visibili sono già pubblicati.
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPubblicaDialog(false)}>Annulla</Button>
              <Button 
                onClick={pubblicaTurniMultipli} 
                disabled={turniSelezionati.length === 0}
                className="bg-emerald-600 hover:bg-emerald-700">
                ✓ Pubblica {turniSelezionati.length > 0 && `(${turniSelezionati.length})`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Dialog Replica Settimana */}
      {isCoordOrAdmin() && (
        <Dialog open={showReplicaDialog} onOpenChange={setShowReplicaDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Replica Turni Settimana Precedente</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Questa funzione replicherà tutti i turni della settimana precedente nella settimana corrente.
              </p>
              <p className="text-sm text-blue-600 bg-blue-50 p-3 rounded-lg">
                ℹ️ I turni replicati verranno creati come <strong>bozza</strong> e dovrai pubblicarli manualmente.
              </p>
              <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                ⚠️ Se esistono già turni per lo stesso operatore nello stesso giorno, verranno ignorati.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReplicaDialog(false)}>Annulla</Button>
              <Button 
                onClick={replicaTurniSettimana}
                className="bg-orange-600 hover:bg-orange-700">
                📋 Replica Settimana
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      
    </div>
  );
}
